import { useEffect, useRef } from 'react';

import { updateVideoProgress } from '../../api/lfApiVideo';
import { parseSearchParam } from './parseSearchParam';

const STEP = 10;

function _quantize(progress) {
  if (!Number.isFinite(progress) || progress <= 0) return 0;
  return Math.min(100, Math.floor(progress / STEP) * STEP);
}

/**
 * Persist video playback progress to the backend in 10% steps as the
 * child watches. The YouTube player reports raw currentTime/duration
 * every 500ms via VideoPlayer; this hook quantizes that into 10%
 * steps and POSTs `/video/progress` once per crossing.
 *
 * Why: the parent summary email filters watch_history rows by
 * progress >= 50 (history.WATCH_HISTORY_DATA_PROGRESS_MIN_THREADHOLD).
 * Without playback-driven persistence, search videos only reach the
 * email when the child clicks all the way through the Q&A, so a kid
 * who watched the whole video but skipped the quiz never showed up.
 *
 * Monotonic: replays of the same step are ignored. The backend's
 * `update_progress` is also monotonic (only writes when the new
 * progress > current), so re-POSTs of an old step are a no-op.
 */
export function useVideoPlaybackPersistence({
  videoId,
  channelId,
  videoProgress,
  setError,
}) {
  const lastRef = useRef({ videoId: null, step: 0 });

  useEffect(() => {
    if (!videoId || !channelId) return;

    if (lastRef.current.videoId !== videoId) {
      lastRef.current = { videoId, step: 0 };
    }

    const stepNow = _quantize(videoProgress);
    if (stepNow <= lastRef.current.step) return;

    lastRef.current = { videoId, step: stepNow };

    const { studyPlanId, lessonId, topicId } = parseSearchParam();
    updateVideoProgress(
      videoId,
      channelId,
      studyPlanId,
      lessonId,
      topicId,
      stepNow,
      setError,
    ).catch((error) => {
      console.error('Error persisting playback progress:', error);
    });
  }, [videoId, channelId, videoProgress, setError]);
}
